import React, { useEffect, useState, useRef, ReactNode, CSSProperties } from 'react';
import { Portal } from 'components/portal';
import { useTheme } from 'hooks/useTheme';
import styles from './BaseModal.css';

interface Props {
    visible: boolean;
    onClose: () => void;
    children: ReactNode;
    contentStyle?: CSSProperties;
    rotation?: number;
}

const BaseModal = ({ visible, onClose, children, contentStyle, rotation = 0 }: Props) => {
    const theme = useTheme();
    const [renderModal, setRenderModal] = useState(false);
    const [animationState, setAnimationState] = useState<'initial' | 'entering' | 'entered' | 'exiting'>('initial');
    const animationRef = useRef<number | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isRotatedSideways = rotation === 90 || rotation === 270;

    useEffect(() => {
        // Cleanup function
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (visible) {
            // Clear any pending animations
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            setRenderModal(true);
            setAnimationState('initial');
            
            // Use a small timeout to ensure the initial state is painted
            timeoutRef.current = setTimeout(() => {
                setAnimationState('entering');
                timeoutRef.current = setTimeout(() => {
                    setAnimationState('entered');
                }, 150);
            }, 10);
        } else if (renderModal) {
            // Clear any pending animations
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            setAnimationState('exiting');
            timeoutRef.current = setTimeout(() => {
                setRenderModal(false);
                setAnimationState('initial');
            }, 150);
        }
    }, [visible]);

    if (!renderModal) {
        return null;
    }

    const getModalClassName = () => {
        const base = styles.modalContainer;
        switch (animationState) {
            case 'initial': return `${base} ${styles.modalInitial}`;
            case 'entering': return `${base} ${styles.modalEntering}`;
            case 'entered': return `${base} ${styles.modalVisible}`;
            case 'exiting': return `${base} ${styles.modalExiting}`;
            default: return `${base} ${styles.modalInitial}`;
        }
    };

    const isBackdropVisible = animationState === 'entering' || animationState === 'entered';

    return (
        <Portal>
            <div 
                className={styles.overlay}
                style={{ pointerEvents: visible ? 'auto' : 'none' }}
            >
                <div 
                    className={styles.backdropContainer}
                    onClick={onClose}
                >
                    <div 
                        className={`${styles.backdrop} ${isBackdropVisible ? styles.backdropVisible : ''}`}
                        style={{ backgroundColor: theme.colors.modalOverlay }}
                    />
                </div>

                <div
                    className={getModalClassName()}
                    style={{
                        backgroundColor: theme.colors.modalBackground,
                        width: '80vw',
                        height: '80vh',
                    }}
                >
                    <div
                        className={styles.contentContainer}
                        style={{
                            transform: rotation ? `rotate(${rotation}deg)` : undefined,
                            width: isRotatedSideways ? '80vh' : '100%',
                            height: isRotatedSideways ? '80vw' : '100%',
                            ...contentStyle,
                        }}
                    >
                        {children}
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default BaseModal;
